import React, { useState, useEffect } from 'react';
import { Save, X, Trash2, FileText, Package, Box, Truck, Clock } from 'lucide-react';
import api from '../services/api';
import '../styles/JobForm.css';

// Sub-components
import BasicInfoTab from './tabs/BasicInfoTab';
import CargoTab from './tabs/CargoTab';
import ContainerTab from './tabs/ContainerTab';
import DocumentsTab from './tabs/DocumentsTab';
import InvoiceTab from './tabs/InvoiceTab';
import BillOfEntryTab from './tabs/Billofentrytab';
import TransportTab from './tabs/TransportTab';
import AuditLogTab from './tabs/Auditlogtab';

const JobForm = ({ jobId, onClose, onSaved }) => {
    const [activeTab, setActiveTab] = useState('basic');
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isGeneratingCode, setIsGeneratingCode] = useState(false);
    const [masterData, setMasterData] = useState({});
    const [errors, setErrors] = useState({});

    const [formData, setFormData] = useState({
        JobID: null,
        JobCode: '',
        JobTypeID: null,
        JobDate: new Date().toISOString().split('T')[0],
        ConsignerID: null,
        ConsigneeID: null,
        InvoiceToID: null,
        ShipperID: null,
        EmployeeID: null,
        VesselID: null,
        VoyageNo: '',
        AirlineID: null,
        Flight: '',
        MAWB: '',
        HAWB: '',
        MBL: '',
        HBL: '',
        LoadPortID: null,
        DestinationPortID: null,
        ReceiptPlaceID: null,
        DeliveryPlaceID: null,
        BLDate: new Date().toISOString().split('T')[0],
        SailingDate: new Date().toISOString().split('T')[0],
        ArrivalDate: new Date().toISOString().split('T')[0],
        CollectionDate: new Date().toISOString().split('T')[0],
        DeliveryDate: new Date().toISOString().split('T')[0],
        TransporterID: null,
        CarrierID: null,
        TruckRegNo: '',
        DriverDetails: '',
        IPTNo: '',
        RefNo: '',
        DeliveryOrderNo: '',
        CustRef: '',
        GrossWeight: '',
        Volume: '',
        Package: '',
        BLStatus: '',
        Freight: '',
        StausInvoiceType: 'CR',
        CLFValue: '',
        DepositAmount: '',
        RefundAmount: '',
        ShippingInstruction: '',
        DeliveryInstructions: '',
        CollectionInstructions: '',
        CountryofOriginID: null,
        MRN: '',
        RotationNo: '',
        CollectionPoint: '',
        DeliveryNote: '',
        DeliveryPoint: '',
        Remarks: '',
        TotalTax: 0,
        StatusSub: false,
        MainJobID: null,
        QuotationID: null,
        cargoDescriptions: [],
        containerDetails: [],
        invoiceDetails: [],
        billOfEntry: [],
        auditLog: []
    });

    // ── on mount ──────────────────────────────────────────────────────────────
    useEffect(() => {
        const init = async () => {
            await loadMasterData();
            if (jobId) {
                await loadJobData(jobId);
            } else {
                initializeEmptyTables();
            }
        };
        init();
    }, [jobId]);

    // ── empty table rows for new jobs ─────────────────────────────────────────
    const initializeEmptyTables = () => {
        setFormData(prev => ({
            ...prev,
            cargoDescriptions: [{
                CargoDescriptionID: null, Mark: '', Description: '',
                Weight: 0, Volume: 0, Packages: 0, GrossWeight: 0
            }],
            containerDetails: [{
                JContainerDetailID: null, ContainerTypeID: null,
                ContainerNo: '', SealNo: '', Description: ''
            }],
            invoiceDetails: [{
                InvoiceID: null, RevenueTypeID: null,
                ProvisionCurrencyID: null, ProvisionExchangeRate: 1,
                ProvisionForeign: 0, ProvisionHome: 0, ProvisionRate: 0,
                SalesCurrencyID: null, SalesExchangeRate: 1,
                SalesForeign: 0, SalesHome: 0, SalesRate: 0,
                Cost: 0, SupplierID: null, RevenueCode: '',
                Quantity: 0, UnitID: null, TaxID: null,
                TaxPer: 0, TaxAmt: 0, TaxAmtF: 0, AmtInWords: ''
            }],
            billOfEntry: [{
                BillOfEntryID: null, BillOfEntry: '',
                BillofEntryDate: new Date().toISOString().split('T')[0],
                ShippingAgentID: null
            }],
            auditLog: []
        }));
    };

    // ── load master data ──────────────────────────────────────────────────────
    const loadMasterData = async () => {
        try {
            console.log('📥 Loading master data...');
            const response = await api.get('/JobGeneration.php/masters');
            if (response.data.success) {
                setMasterData(response.data.data);
                console.log('✅ Master data loaded');
            } else {
                throw new Error(response.data.message || 'Failed to load master data');
            }
        } catch (err) {
            console.error('❌ Error loading master data:', err);
            alert('Failed to load dropdown data: ' + (err.response?.data?.message || err.message));
        }
    };

    // ── load existing job ─────────────────────────────────────────────────────
    const loadJobData = async (id) => {
        try {
            setIsLoading(true);
            console.log('📥 Loading job:', id);
            const response = await api.get(`/JobGeneration.php/${id}`);
            if (response.data.success) {
                setFormData(response.data.data);
                console.log('✅ Job loaded');
            } else {
                throw new Error(response.data.message || 'Failed to load job');
            }
        } catch (err) {
            console.error('❌ Error loading job:', err);
            alert('Failed to load job: ' + (err.response?.data?.message || err.message));
        } finally {
            setIsLoading(false);
        }
    };

    // ── UPDATED: Generate job code with sub-job support ───────────────────────
    const generateJobCode = async (jobTypeId, jobDate, mainJobId = null) => {
        if (!jobTypeId || !jobDate) return;

        // For sub-jobs, require main job ID
        if (formData.StatusSub && !mainJobId) {
            console.log('⚠️ Sub-job requires main job selection');
            return;
        }

        try {
            setIsGeneratingCode(true);
            console.log('🔢 Generating job code for type:', jobTypeId, 'date:', jobDate, 'mainJobId:', mainJobId);

            let url = `/JobGeneration.php/next-code?jobtypeid=${jobTypeId}&jobdate=${jobDate}`;
            if (mainJobId) {
                url += `&mainjobid=${mainJobId}`;
            }

            const response = await api.get(url);

            if (response.data.success) {
                const newCode = response.data.data.jobCode;
                console.log('✅ Generated code:', newCode);
                setFormData(prev => ({ ...prev, JobCode: newCode }));
            }
        } catch (err) {
            console.error('❌ Error generating job code:', err);
        } finally {
            setIsGeneratingCode(false);
        }
    };

    // ── input change handler ──────────────────────────────────────────────────
    const handleInputChange = (field, value) => {
        setFormData(prev => {
            const updated = { ...prev, [field]: value };

            // Auto-fill consigner/consignee when Invoice To is selected (only if NOT sub-job)
            if (field === 'InvoiceToID' && value && !prev.StatusSub) {
                updated.ConsignerID = value;
                updated.ConsigneeID = value;
            }

            return updated;
        });

        // Clear validation error for this field
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }

        // ── AUTO-GENERATE JOB CODE ──────────────────────────────────────────
        if (field === 'JobTypeID') {
            setFormData(prev => {
                const jobDate = prev.JobDate;
                const mainJobId = prev.MainJobID;
                generateJobCode(value, jobDate, mainJobId);
                return prev;
            });
        }

        if (field === 'JobDate') {
            setFormData(prev => {
                const jobTypeId = prev.JobTypeID;
                const mainJobId = prev.MainJobID;
                if (jobTypeId) {
                    generateJobCode(jobTypeId, value, mainJobId);
                }
                return prev;
            });
        }

        // Regenerate code when MainJobID changes
        if (field === 'MainJobID') {
            setFormData(prev => {
                const jobTypeId = prev.JobTypeID;
                const jobDate = prev.JobDate;
                if (jobTypeId && jobDate && value) {
                    generateJobCode(jobTypeId, jobDate, value);
                }
                return prev;
            });
        }
    };

    // ── child table changes ───────────────────────────────────────────────────
    const handleChildDataChange = (childTable, data) => {
        setFormData(prev => ({ ...prev, [childTable]: data }));
    };

    // ── validation ────────────────────────────────────────────────────────────
    const validateForm = () => {
        const newErrors = {};
        if (!formData.JobCode)    newErrors.JobCode    = 'Job code is required';
        if (!formData.JobTypeID)  newErrors.JobTypeID  = 'Job type is required';
        if (!formData.JobDate)    newErrors.JobDate    = 'Job date is required';
        if (!formData.InvoiceToID) newErrors.InvoiceToID = 'Invoice To is required';
        
        // Validate sub-job requirements
        if (formData.StatusSub && !formData.MainJobID) {
            newErrors.MainJobID = 'Main job is required for sub-jobs';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // ── save ──────────────────────────────────────────────────────────────────
    const handleSave = async () => {
        if (!validateForm()) {
            alert('Please fill all required fields');
            return;
        }
        try {
            setIsSaving(true);
            console.log('💾 Saving job...');
            let response;
            if (jobId) {
                response = await api.put('/JobGeneration.php', formData);
            } else {
                response = await api.post('/JobGeneration.php', formData);
            }
            if (response.data.success) {
                alert(jobId ? 'Job updated successfully' : 'Job created successfully');
                if (onSaved) onSaved(response.data.data.jobId);
                if (onClose) onClose();
            } else {
                throw new Error(response.data.message || 'Save failed');
            }
        } catch (err) {
            console.error('❌ Error saving job:', err);
            alert('Error saving job: ' + (err.response?.data?.message || err.message));
        } finally {
            setIsSaving(false);
        }
    };

    // ── delete ────────────────────────────────────────────────────────────────
    const handleDelete = async () => {
        if (!jobId) return;
        if (!window.confirm('Are you sure you want to delete this job?')) return;
        try {
            const response = await api.delete(`/JobGeneration.php/${jobId}`);
            if (response.data.success) {
                alert('Job deleted successfully');
                if (onClose) onClose();
                if (onSaved) onSaved();
            } else {
                throw new Error(response.data.message || 'Delete failed');
            }
        } catch (err) {
            console.error('❌ Error deleting job:', err);
            alert('Error deleting job: ' + (err.response?.data?.message || err.message));
        }
    };

    // ── tabs ──────────────────────────────────────────────────────────────────
    const tabs = [
        { id: 'basic',      label: 'Basic Info',    icon: FileText },
        { id: 'cargo',      label: 'Cargo',          icon: Package  },
        { id: 'container',  label: 'Containers',     icon: Box      },
        { id: 'documents',  label: 'Documents',      icon: FileText },
        { id: 'invoice',    label: 'Invoice',        icon: FileText },
        { id: 'billofentry',label: 'Bill of Entry',  icon: FileText },
        { id: 'transport',  label: 'Transport',      icon: Truck    },
        { id: 'audit',      label: 'Audit Log',      icon: Clock    }
    ];

    if (isLoading) {
        return (
            <div className="job-form-loading">
                <div className="spinner"></div>
                <p>Loading job data...</p>
            </div>
        );
    }

    return (
        <div className="job-form-container">
            {/* Header */}
            <div className="job-form-header">
                <div className="job-form-title">
                    <h2>{jobId ? `Edit Job - ${formData.JobCode}` : 'New Job'}</h2>
                    {formData.StatusSub && (
                        <span className="subjob-badge-header">SUB-JOB</span>
                    )}
                    {formData.InvoiceNo && (
                        <span className="invoice-badge">Invoice: {formData.InvoiceNo}</span>
                    )}
                    {isGeneratingCode && (
                        <span className="generating-badge">⏳ Generating code...</span>
                    )}
                </div>
                <div className="job-form-actions">
                    <button
                        className="btn btn-primary"
                        onClick={handleSave}
                        disabled={isSaving || isGeneratingCode}
                    >
                        <Save size={18} />
                        {isSaving ? 'Saving...' : 'Save'}
                    </button>
                    {jobId && (
                        <button
                            className="btn btn-danger"
                            onClick={handleDelete}
                            disabled={!!formData.InvoiceNo}
                        >
                            <Trash2 size={18} />
                            Delete
                        </button>
                    )}
                    <button className="btn btn-secondary" onClick={onClose}>
                        <X size={18} />
                        Close
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div className="job-form-tabs">
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        <tab.icon size={18} />
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            <div className="job-form-content">
                {activeTab === 'basic' && (
                    <BasicInfoTab
                        formData={formData}
                        masterData={masterData}
                        errors={errors}
                        isGeneratingCode={isGeneratingCode}
                        onChange={handleInputChange}
                    />
                )}
                {activeTab === 'cargo' && (
                    <CargoTab
                        data={formData.cargoDescriptions}
                        onChange={(data) => handleChildDataChange('cargoDescriptions', data)}
                    />
                )}
                {activeTab === 'container' && (
                    <ContainerTab
                        data={formData.containerDetails}
                        masterData={masterData}
                        onChange={(data) => handleChildDataChange('containerDetails', data)}
                    />
                )}
                {activeTab === 'documents' && (
                    <DocumentsTab
                        formData={formData}
                        masterData={masterData}
                        onChange={handleInputChange}
                    />
                )}
                {activeTab === 'invoice' && (
                    <InvoiceTab
                        data={formData.invoiceDetails}
                        masterData={masterData}
                        jobDate={formData.JobDate}
                        onChange={(data) => handleChildDataChange('invoiceDetails', data)}
                        onTaxChange={(totalTax) => handleInputChange('TotalTax', totalTax)}
                    />
                )}
                {activeTab === 'billofentry' && (
                    <BillOfEntryTab
                        data={formData.billOfEntry}
                        masterData={masterData}
                        onChange={(data) => handleChildDataChange('billOfEntry', data)}
                    />
                )}
                {activeTab === 'transport' && (
                    <TransportTab
                        formData={formData}
                        masterData={masterData}
                        onChange={handleInputChange}
                    />
                )}
                {activeTab === 'audit' && (
                    <AuditLogTab
                        data={formData.auditLog}
                        onChange={(data) => handleChildDataChange('auditLog', data)}
                    />
                )}
            </div>

            {/* Footer */}
            <div className="job-form-footer">
                <div className="job-info">
                    {formData.CreatedAt && (
                        <span>Created: {new Date(formData.CreatedAt).toLocaleString()}</span>
                    )}
                    {formData.UpdatedAt && (
                        <span>Updated: {new Date(formData.UpdatedAt).toLocaleString()}</span>
                    )}
                </div>
            </div>
        </div>
    );
};

export default JobForm;